import type { IConfig } from "config/IConfig";
import type { NextRouter } from "next/router";
import type { UrlObject } from "url";
import { ParsedUrlQuery } from "querystring";

interface IUrlsServiceParams {
    seo?: boolean;
    query?: ParsedUrlQuery;
}

interface ILandingOptions {
    isEditProfileModalOpen?: boolean;
    isVerifyPhoneModalVisible?: boolean;
    isAskVerifyPhoneModalVisible?: boolean;
    isConfirmChangeVerifiedPhoneModalVisible?: boolean;
    isProfileMenuOpen?: boolean;
    isMyMedalsModalOpen?: boolean;
    phone?: string;
    options?: IUrlsServiceParams;
}

interface ILoginOptions {
    options?: IUrlsServiceParams;
}

const PATH_PROFILE_EDIT = "edit";
const PATH_PROFILE_VERIFY_PHONE = "verify-phone";
const PATH_PROFILE_ASK_VERIFY_PHONE = "ask-phone-verification";
const PATH_CONFIRM_CHANGE_VERIFIED_PHONE = "confirm-change-verified-phone";
const PATH_PROFILE_MENU = "profile-menu";
const PATH_MY_TIENDEO = "my-tiendeo";
const PATH_HELP = "help";
const PATH_LOGIN = "login";
const PATH_CONTACT_US = "contact-us";
const PATH_LOYALTY_CARDS = "loyalty-cards";
const PATH_MY_MEDALS = "my-medals";
const PATH_SAVED_PAGES = "saved-pages";
const PATH_WEB_NOTIFICATIONS = "notifications";
const PATH_WEB_NOTIFICATIONS_MANAGEMENT = "notifications-management";

interface IHelpOptions {
    isContactUsOpen?: boolean;
    options?: IUrlsServiceParams;
}

export default class UrlsService {
    private readonly countryCode: string;
    private readonly pathProfile: string;
    private readonly pathProfileEdit: string;
    private readonly pathProfileVerifyPhone: string;
    private readonly pathAskToVerifyPhone: string;
    private readonly pathConfirmChangeVerifiedPhone: string;
    private readonly pathProfileMenu: string;
    private readonly pathMyTiendeo: string;
    private readonly pathHelp: string;
    private readonly pathLogin: string;
    private readonly pathContactUs: string;
    private readonly pathLoyaltyCards: string;
    private readonly pathMyMedals: string;
    private readonly pathSavedPages: string;
    private readonly pathWebNotifications: string;
    private readonly pathWebNotificationsManagement: string;
    private readonly urlTiendeo: string;

    constructor(config: IConfig) {
        this.countryCode = config.country_code;
        this.pathProfile = config.path_profile;
        this.pathProfileEdit = config.path_profile_edit;
        this.pathProfileVerifyPhone = config.path_profile_verify_phone;
        this.pathAskToVerifyPhone = config.path_profile_ask_verify_phone;
        this.pathConfirmChangeVerifiedPhone = config.path_confirm_change_verified_phone;
        this.pathProfileMenu = config.path_profile_menu;
        this.pathMyTiendeo = config.path_my_tiendeo;
        this.pathHelp = config.path_help;
        this.pathLogin = config.path_login;
        this.pathContactUs = config.path_contact_us;
        this.pathLoyaltyCards = config.path_loyalty_cards;
        this.pathMyMedals = config.path_my_medals;
        this.pathSavedPages = config.path_savedPages;
        this.pathWebNotifications = config.path_web_notifications;
        this.pathWebNotificationsManagement = config.path_web_notifications_management;
        this.urlTiendeo = config.url_tiendeo;
    }

    WebSite(): UrlObject {
        return {
            pathname: this.urlTiendeo,
        };
    }

    Landing({
        isEditProfileModalOpen = undefined,
        isVerifyPhoneModalVisible = undefined,
        isAskVerifyPhoneModalVisible = undefined,
        isConfirmChangeVerifiedPhoneModalVisible = undefined,
        isProfileMenuOpen = undefined,
        isMyMedalsModalOpen = undefined,
        phone = undefined,
        options: { seo } = {},
    }: ILandingOptions): UrlObject {
        if (seo) {
            if (isVerifyPhoneModalVisible) {
                return {
                    pathname: `/${this.pathProfile}`,
                    query: {
                        edit: this.pathProfileEdit,
                        verifyPhone: this.pathProfileVerifyPhone,
                        phone,
                    },
                };
            } else if (isAskVerifyPhoneModalVisible) {
                return {
                    pathname: `/${this.pathProfile}`,
                    query: {
                        edit: this.pathProfileEdit,
                        askVerifyPhone: this.pathAskToVerifyPhone,
                        phone,
                    },
                };
            } else if (isConfirmChangeVerifiedPhoneModalVisible) {
                return {
                    pathname: `/${this.pathProfile}`,
                    query: {
                        edit: this.pathProfileEdit,
                        confirmChangeVerifiedPhone: this.pathConfirmChangeVerifiedPhone,
                        phone,
                    },
                };
            } else if (isEditProfileModalOpen) {
                return {
                    pathname: `/${this.pathProfile}`,
                    query: {
                        edit: this.pathProfileEdit,
                    },
                };
            } else if (isProfileMenuOpen) {
                return {
                    pathname: `/${this.pathProfile}`,
                    query: {
                        profileMenu: this.pathProfileMenu,
                    },
                };
            } else if (isMyMedalsModalOpen) {
                return {
                    pathname: `/${this.pathProfile}`,
                    query: {
                        myMedals: this.pathMyMedals,
                    },
                };
            }

            return {
                pathname: `/${this.pathProfile}`,
            };
        }

        return {
            pathname: "/",
            query: {
                countryCode: this.countryCode,
                edit: isEditProfileModalOpen ? PATH_PROFILE_EDIT : undefined,
                verifyPhone: isVerifyPhoneModalVisible ? PATH_PROFILE_VERIFY_PHONE : undefined,
                askVerifyPhone: isAskVerifyPhoneModalVisible
                    ? PATH_PROFILE_ASK_VERIFY_PHONE
                    : undefined,
                PATH_CONFIRM_CHANGE_VERIFIED_PHONE: isConfirmChangeVerifiedPhoneModalVisible
                    ? PATH_CONFIRM_CHANGE_VERIFIED_PHONE
                    : undefined,
                profileMenu: isProfileMenuOpen ? PATH_PROFILE_MENU : undefined,
                myMedals: isMyMedalsModalOpen ? PATH_MY_MEDALS : undefined,
                phone,
            },
        };
    }

    MyTiendeo({ options: { seo } = {} }: { options?: IUrlsServiceParams }): UrlObject {
        if (seo) {
            return {
                pathname: `/${this.pathProfile}/${this.pathMyTiendeo}`,
            };
        }

        return {
            pathname: `/${PATH_MY_TIENDEO}`,
            query: {
                countryCode: this.countryCode,
            },
        };
    }

    LoyaltyCards({ options: { seo } = {} }: { options?: IUrlsServiceParams }): UrlObject {
        if (seo) {
            return {
                pathname: `/${this.pathProfile}/${this.pathLoyaltyCards}`,
            };
        }

        return {
            pathname: `/${PATH_LOYALTY_CARDS}`,
            query: {
                countryCode: this.countryCode,
            },
        };
    }

    MyMedals({ options: { seo } = {} }: { options?: IUrlsServiceParams }): UrlObject {
        if (seo) {
            return {
                pathname: `/${this.pathProfile}/${this.pathMyMedals}`,
            };
        }

        return {
            pathname: `/${PATH_MY_MEDALS}`,
            query: {
                countryCode: this.countryCode,
            },
        };
    }

    SavedPages({ options: { seo } = {} }: { options?: IUrlsServiceParams }): UrlObject {
        if (seo) {
            return {
                pathname: `/${this.pathProfile}/${this.pathSavedPages}`,
            };
        }

        return {
            pathname: `/${PATH_SAVED_PAGES}`,
            query: {
                countryCode: this.countryCode,
            },
        };
    }

    Notifications({ options: { seo } = {} }: { options?: IUrlsServiceParams }): UrlObject {
        if (seo) {
            return {
                pathname: `/${this.pathProfile}/${this.pathWebNotifications}`,
            };
        }

        return {
            pathname: `/${PATH_WEB_NOTIFICATIONS}`,
            query: {
                countryCode: this.countryCode,
            },
        };
    }

    NotificationsManagement({
        options: { seo, query } = {},
    }: {
        options?: IUrlsServiceParams;
    }): UrlObject {
        if (seo) {
            return {
                pathname: `/${this.pathProfile}/${this.pathWebNotificationsManagement}/${query.hash}`,
            };
        }

        return {
            pathname: `/${PATH_WEB_NOTIFICATIONS_MANAGEMENT}`,
            query: {
                countryCode: this.countryCode,
            },
        };
    }

    Login({ options: { seo } = {} }: ILoginOptions): UrlObject {
        if (seo) {
            return {
                pathname: `/${this.pathProfile}/${this.pathLogin}`,
            };
        }

        return {
            pathname: `/${PATH_LOGIN}`,
            query: {
                countryCode: this.countryCode,
            },
        };
    }

    Help({ isContactUsOpen = undefined, options: { seo } = {} }: IHelpOptions): UrlObject {
        if (seo) {
            if (isContactUsOpen) {
                return {
                    pathname: `/${this.pathProfile}/${this.pathHelp}`,
                    query: {
                        contactUs: this.pathContactUs,
                    },
                };
            }

            return {
                pathname: `/${this.pathProfile}/${this.pathHelp}`,
            };
        }

        return {
            pathname: `/${PATH_HELP}`,
            query: {
                countryCode: this.countryCode,
                contactUs: isContactUsOpen ? PATH_CONTACT_US : undefined,
            },
        };
    }

    ContactUsEmail(
        destinationEmail: string,
        subject: string,
        userId: string,
        isBrowser: boolean,
    ): string {
        if (!isBrowser) return "";

        const subjectEncoded = `subject=${encodeURIComponent(subject)}`;

        const body = `body=${encodeURIComponent(
            [
                `EndUserId: ${userId || "anonymous"}`,
                `Country: ${this.countryCode}`,
                `Origin: ${window.location.href}`,
                "----------------\n\n",
            ]
                .filter((item) => !!item)
                .join("\n"),
        )}`;
        return `mailto:${destinationEmail}?${subjectEncoded}&${body}`;
    }

    OpenMenu(
        asPath: string,
        pathname: NextRouter["pathname"],
        query: NextRouter["query"],
        { options: { seo } = {} }: { options?: IUrlsServiceParams },
    ): UrlObject {
        if (seo) {
            return {
                pathname: asPath,
                query: {
                    profileMenu: this.pathProfileMenu,
                },
            };
        }

        return {
            pathname,
            query: {
                ...query,
                profileMenu: PATH_PROFILE_MENU,
            },
        };
    }

    CloseMenu(
        asPath: string,
        pathname: NextRouter["pathname"],
        query: NextRouter["query"],
        { options: { seo } = {} }: { options?: IUrlsServiceParams },
    ): UrlObject {
        if (seo) {
            return {
                pathname: asPath.split("?")[0],
                query: {},
            };
        }

        const { profileMenu, ...restQuery } = query;

        return {
            pathname,
            query: restQuery,
        };
    }

    isEditProfile = (pathName: string) =>
        pathName.includes(this.pathProfileEdit) || pathName.includes(PATH_PROFILE_EDIT);
    isMyMedalsWebview = (pathName: string) =>
        pathName.includes(this.pathMyMedals) || pathName.includes(PATH_MY_MEDALS);
    isVerifyPhone = (pathName: string) =>
        pathName.includes(this.pathProfileVerifyPhone) ||
        pathName.includes(PATH_PROFILE_VERIFY_PHONE);
    isAskToVerifyPhone = (pathName: string) =>
        pathName.includes(this.pathAskToVerifyPhone) ||
        pathName.includes(PATH_PROFILE_ASK_VERIFY_PHONE);
    isConfirmChangeVerifiedPhone = (pathName: string) =>
        pathName.includes(this.pathConfirmChangeVerifiedPhone) ||
        pathName.includes(PATH_CONFIRM_CHANGE_VERIFIED_PHONE);
    isProfileMenu = (pathName: string) =>
        pathName.includes(this.pathProfileMenu) || pathName.includes(PATH_PROFILE_MENU);
    isContactUs = (pathName: string) =>
        pathName.includes(this.pathContactUs) || pathName.includes(PATH_CONTACT_US);

    public get PathMyTiendeo(): string {
        return "/" + PATH_MY_TIENDEO;
    }

    public get PathHelp(): string {
        return "/" + PATH_HELP;
    }
}
